import React, { ChangeEvent, useState, useContext, useEffect } from 'react';
import { GridRowParams } from '@material-ui/data-grid';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { LojaContext } from '~/context/loja';
import { IBancos } from './types';
import Loja from '~/components/Loja';
import { Container, TipoConta } from './styles';
import defaultData from './defaultData/defaultData.json';
import { Col, Row } from 'react-bootstrap';
import FormDefault from '~/components/FormDefault';
import api from '~/services/api';
import ToggleDefault from '~/components/ToggleDefault';
import { Form } from 'react-bootstrap';
import {
  InputText,
  InputMoney,
  InputSelect,
  InputDate,
} from '~/components/NovosInputs';
import Search from '~/components/Search';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { schema } from './Validations';
import { transformAsCurrency, formatCurrencyAsText } from '~/utils/functions';
import { createDateWithoutTimezone } from '~/utils/createDateWithoutTimezone ';
import DefaultLoader from '~/components/DefaultLoader';

const ContaBancaria: React.FC = () => {
  const {
    register,
    control,
    reset,
    getValues,
    setValue,
    clearErrors,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    reValidateMode: 'onBlur',
  });
  const [loader, setLoader] = useState<boolean>(false);
  const [isUpdate, setUpdate] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState(true);
  const { loja } = useContext(LojaContext);
  const [lojaBanco, setLojaBanco] = useState<number>(loja.cod_loja);
  const [optionsBanco, setoptionsBanco] = useState([]);
  const [corrente, setCorrente] = useState<boolean>(false);
  const [flgInativa, setFlgInativa] = useState<boolean>(false);
  const [investimento, setInvestimento] = useState<boolean>(false);
  const [caixa, setCaixa] = useState<boolean>(false);
  const [cofre, setCofre] = useState<boolean>(false);

  /**
   * Reseta form para criação de novo registro
   */
  const resetFormData = () => {
    setUpdate(false);
    reset(defaultData);
    setFlgInativa(false);
    setCorrente(false);
    setInvestimento(false);
    setCaixa(false);
    setCofre(false);
    setValue('cod_loja', loja.cod_loja);
    setLojaBanco(loja.cod_loja);
    setTimeout(() => {
      focusInputByName('cod_banco');
    }, 10);
  };

  const onRowClick = async (param: GridRowParams) => {
    const { row } = param;
    const {
      cod_cc,
      cod_banco,
      cod_loja,
      des_banco,
      des_loja,
      des_cc,
      des_agencia,
      val_saldo_inicial,
      dta_saldo_inicial,
      flg_inativa,
    } = row;
    setUpdate(true);

    const { data } = await api.get(`/contabancaria/${cod_cc}`);
    if (data.success && data.data) {
      setLojaBanco(cod_loja);
      setValue('cod_loja', { value: cod_loja, label: des_loja });
      setValue('cod_cc', cod_cc);
      setValue('des_cc', des_cc);
      setValue('des_agencia', des_agencia);
      setValue('val_saldo_inicial', formatCurrencyAsText(val_saldo_inicial));
      setFlgInativa(flg_inativa);
      setValue('cod_banco', {
        value: cod_banco,
        label: `${cod_banco.toString().padStart(3, '0')} - ${des_banco}`,
      });
      setValue(
        'dta_saldo_inicial',
        format(createDateWithoutTimezone(dta_saldo_inicial), 'yyy-MM-dd'),
      );

      for (let i = 0; i < data.data.length; i++) {
        switch (data.data[i].tipo_cc) {
          case 0:
            setCorrente(true);
            break;
          case 1:
            setInvestimento(true);
            break;
          case 2:
            setCaixa(true);
            break;
          case 3:
            setCofre(true);
            break;
          default:
            break;
        }
      }
    }
    setLoader(false);

    setShowSearch(false);
  };

  /**
   * Listagem dos bancos no select
   */
  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/bancos`);

      if (data.success) {
        const optionsBancos = data.data.map((banco: IBancos) => ({
          label: `${banco.cod_banco} - ${banco.des_banco}`,
          value: banco.cod_banco,
        }));

        setoptionsBanco(optionsBancos);
      }
    })();
  }, []);

  const onDelete = async () => {
    const { cod_cc } = getValues();
    const { data } = await api.delete(`/conta/${cod_cc}`);

    if (data.success) {
      resetFormData();
      setShowSearch(true);
      setUpdate(false);
      toast.warning('Conta Inativada com Sucesso');
    }
  };

  const onNew = () => {
    resetFormData();
    setUpdate(false);
    setShowSearch(false);
    setTimeout(() => {
      focusInputByName('cod_banco');
    }, 50);
  };

  const focusInputByName = (inputName: string) => {
    const inputElement = document.querySelector<HTMLInputElement>(
      `input[name="${inputName}"]`,
    );
    if (inputElement) {
      inputElement.click();
      inputElement.focus();
    }
  };

  const onSave = handleSubmit(async (data1) => {
    const { cod_cc } = getValues();
    if (!corrente && !investimento && !caixa && !cofre) {
      return toast.warning('Tipo de conta deve ser informado');
    }
    try {
      const dataSaldoInicial = format(
        createDateWithoutTimezone(data1.dta_saldo_inicial),
        'yyy-MM-dd',
      );
      const requestData = {
        cod_loja: data1.cod_loja,
        cod_banco: data1.cod_banco.value,
        des_agencia: data1.des_agencia,
        des_cc: data1.des_cc,
        val_saldo_inicial: transformAsCurrency(data1.val_saldo_inicial || '0'),
        dta_saldo_inicial: dataSaldoInicial,
        flg_inativa: flgInativa,
        tipoConta: {
          corrente,
          investimento,
          caixa,
          cofre,
        },
      };
      if (isUpdate) {
        const { data } = await api.put(`/conta/${cod_cc}`, requestData);

        if (data.success) {
          toast.success('Conta Atualizada com Sucesso');
          resetFormData();
          setShowSearch(true);
        } else {
          toast.warning(data.message);
        }
      } else {
        const { data } = await api.post('/contabancaria', requestData);

        if (data.success) {
          toast.success('Conta Cadastrada com Sucesso');
          resetFormData();
          setShowSearch(false);
        } else {
          toast.warning(data.message);
        }
      }
    } finally {
      setLoader(false);
    }
  });

  if (loader) {
    return <DefaultLoader />;
  }

  return (
    <Container>
      {showSearch && (
        <Search codTela={40} newData={() => onNew()} onRowClick={onRowClick} />
      )}
      {!showSearch && (
        <FormDefault
          codTela={40}
          title="Cadastro de Conta Bancária"
          codigoRegistro={[{ value: watch('cod_cc'), des_campo: 'Código' }]}
          onSave={async () => {
            await onSave();
          }}
          onCancel={() => {
            setShowSearch(true);
            resetFormData();
          }}
          isUpdate={isUpdate}
          onNew={onNew}
          isDelete={false}
          onDelete={onDelete}
          deleteMessage="Conta bancária será inativada. Deseja continuar?"
          onClearFields={resetFormData}
          onReturnSearch={() => {
            setShowSearch(true);
            resetFormData();
          }}
        >
          <>
            <Row>
              <Col sm={12} md={6} style={{ marginTop: '12px' }}>
                <Loja
                  selectedLoja={lojaBanco}
                  IsInvalid={false}
                  onChange={(e: number) => {
                    setValue('cod_loja', e);
                    setLojaBanco(e);
                  }}
                  isDisabled={isUpdate}
                />
              </Col>
              <Col sm={12} md={6}>
                <TipoConta label="Tipo de Conta *">
                  <div
                    onClick={() => {
                      setCorrente(!corrente);
                    }}
                  >
                    <Form.Check
                      className="ButtonCheck"
                      type="checkbox"
                      label="Corrente"
                      checked={corrente}
                      onChange={() => null}
                      style={{
                        backgroundColor: corrente ? '#57069e' : '#8850bf',
                        cursor: 'pointer',
                      }}
                    />
                  </div>
                  <div
                    onClick={() => {
                      setInvestimento(!investimento);
                    }}
                  >
                    <Form.Check
                      className="ButtonCheck"
                      type="checkbox"
                      label="Investimento"
                      checked={investimento}
                      onChange={() => null}
                      style={{
                        backgroundColor: investimento ? '#57069e' : '#8850bf',
                        cursor: 'pointer',
                      }}
                    />
                  </div>
                  <div
                    onClick={() => {
                      setCaixa(!caixa);
                    }}
                  >
                    <Form.Check
                      className="ButtonCheck"
                      type="checkbox"
                      label="Caixa"
                      checked={caixa}
                      onChange={() => null}
                      style={{
                        backgroundColor: caixa ? '#57069e' : '#8850bf',
                        cursor: 'pointer',
                      }}
                    />
                  </div>
                  <div
                    onClick={() => {
                      setCofre(!cofre);
                    }}
                  >
                    <Form.Check
                      className="ButtonCheck"
                      type="checkbox"
                      label="Cofre"
                      checked={cofre}
                      onChange={() => setCofre(!cofre)}
                      style={{
                        backgroundColor: cofre ? '#57069e' : '#8850bf',
                        cursor: 'pointer',
                      }}
                    />
                  </div>
                </TipoConta>
              </Col>
            </Row>
            <Row>
              <Col sm={12} md={8}>
                <InputSelect
                  label="Instituição Bancária *"
                  placeholder="Selecione..."
                  name="cod_banco"
                  register={register}
                  control={control}
                  options={optionsBanco}
                  disabled={isUpdate}
                  isError={!!errors.cod_banco}
                  changeSelected={(val: any) => {
                    clearErrors('cod_banco');
                    setValue('cod_banco', val);
                    focusInputByName('des_agencia');
                  }}
                />
              </Col>

              <Col sm={12} md={2}>
                <InputText
                  label="Agência *"
                  name="des_agencia"
                  register={register}
                  control={control}
                  placeholder="Informe a Agência"
                  caseInput="upper"
                  maxLength={5}
                  isError={!!errors.des_agencia}
                />
              </Col>
              <Col sm={12} md={2}>
                <InputText
                  label="Conta *"
                  name="des_cc"
                  register={register}
                  control={control}
                  placeholder="Informe a Conta"
                  caseInput="upper"
                  maxLength={15}
                  isError={!!errors.des_cc}
                />
              </Col>
            </Row>
            <Row>
              <Col sm={12} md={2}>
                <InputMoney
                  label="Saldo Inicial"
                  name="val_saldo_inicial"
                  min={0.0}
                  max={99999.99}
                  maxLength={9}
                  control={control}
                  register={register}
                  placeholder="0,00"
                  isError={!!errors.val_saldo_inicial}
                />
              </Col>
              <Col sm={12} md={2}>
                <InputDate
                  label="Data do Saldo Inicial *"
                  name="dta_saldo_inicial"
                  register={register}
                  control={control}
                  isError={!!errors.dta_saldo_inicial}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    const { value } = event?.target;
                    clearErrors('dta_saldo_inicial');
                    setValue('dta_saldo_inicial', value);
                  }}
                />
              </Col>
              <Col sm={12} md={2}>
                <ToggleDefault
                  labelText="Inativa?"
                  setChecked={flgInativa}
                  onSwitch={() => {
                    setFlgInativa(!flgInativa);
                  }}
                />
              </Col>
            </Row>
          </>
        </FormDefault>
      )}
    </Container>
  );
};

export default ContaBancaria;
